import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import TradeCard from '../TradeCard';
import {
  FilterNames,
  ListVariant,
  SaleEventFooterVariant,
  SaleEventHeaderVariant,
  TradeStateName,
  TradeStateNew,
  TradeStateNewName,
  GridCardVariant
} from '../../_shared/types/trade';
import { SEARCH_IN } from '../../_shared/constants/search';
import SaleEventHeader from '../SaleEvent/SaleEventHeader';
import SaleEventFooter from '../SaleEvent/SaleEventFooter';
import TradeCardEmpty from '../TradeCardEmpty';
import TradeCardLoading from '../TradeCard/ListTradeCard/TradeCardLoading';
import GridCardsLoading from '../TradeCard/GridTradeCard/GridCardsLoading';
import ROUTES from '../Routes/constants';
import { updateSelectedTrade } from '../../redux/slices/ui/selectedTrades';
import useSelectedTrade from '../../_shared/hooks/trade/useSelectedTrade';

import { StyledTradeGrid, StyledTradeGridWrapper } from './Styles';

const propTypes = {
  testId: PropTypes.string.isRequired,
  tradeSummaries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired
    })
  ),
  saleEventSummaries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number
    })
  ),
  tradeState: PropTypes.oneOfType([PropTypes.object, PropTypes.number]),
  pageNumber: PropTypes.number,
  searchIn: PropTypes.string
};

const defaultProps = {
  tradeSummaries: [],
  saleEventSummaries: [],
  tradeState: undefined,
  pageNumber: 1,
  searchIn: undefined
};

const TradeGrid = ({
  testId,
  tradeSummaries,
  gridTradeList,
  saleEventSummaries,
  tradeSelectionHandler,
  variant,
  tradeState,
  isLoading,
  selectedActiveList,
  pageNumber,
  onConfirmOpen,
  pendingActiveProxy,
  bidModalRef,
  activeNetwork,
  hasNextPage,
  showSaleEvent,
  searchIn,
  isOmp = false
}) => {
  const dispatch = useDispatch();
  const [gridTradeSummaries, setGridTradeSummaries] = useState(gridTradeList);
  const selectedTrade = useSelectedTrade(tradeState);
  // TODO: Uncomment when working on grid view keyboard events
  // const [selectedTradeIndex, setSelectedTradeIndex] = useState(0);

  useEffect(() => {
    setGridTradeSummaries(gridTradeList);
  }, [gridTradeList]);

  useEffect(() => {
    if (variant === ListVariant.GRID) {
      if (gridTradeList.length > 0) {
        dispatch(
          updateSelectedTrade({
            tradeState,
            tradeId: gridTradeList[0].tradeSummary.id
          })
        );
      } else {
        dispatch(
          updateSelectedTrade({
            tradeState,
            tradeId: null
          })
        );
      }
    }
  }, [variant, dispatch, tradeState, gridTradeList]);

  // TODO: Uncomment when working on grid view keyboard events
  // const handleKeyDown = useCallback(
  //   event => {
  //     if (event.key === 'ArrowLeft' && selectedTradeIndex > 0) {
  //       setSelectedTradeIndex(selectedTradeIndex - 1);
  //     } else if (
  //       event.key === 'ArrowRight' &&
  //       selectedTradeIndex < tradeSummaries.length - 1
  //     ) {
  //       setSelectedTradeIndex(selectedTradeIndex + 1);
  //     }
  //   },
  //   [selectedTradeIndex, setSelectedTradeIndex, tradeSummaries]
  // );
  //
  // useEffect(() => {
  //   if (variant === ListVariant.GRID) {
  //     document.addEventListener('keydown', handleKeyDown);
  //   } else {
  //     document.removeEventListener('keydown', handleKeyDown);
  //   }
  //
  //   return () => document.removeEventListener('keydown', handleKeyDown);
  // }, [variant, handleKeyDown]);
  //
  // const onGridCardClick = tradeId => {
  //   const tradeIndex = tradeSummaries.findIndex(trade => trade.id === tradeId);
  //   setSelectedTradeIndex(tradeIndex);
  // };
  //
  // useEffect(() => {
  //   if (tradeSummaries && tradeSummaries.length > 0) {
  //     tradeSelectionHandler(tradeSummaries[selectedTradeIndex].id);
  //   }
  // }, [tradeSelectionHandler, tradeSummaries, selectedTradeIndex]);

  // Note: The following function is only used by ListTradeCard
  const shouldShowSaleEventHeader = tradeSummaryIdx => {
    if (!tradeSummaries[tradeSummaryIdx]?.saleEventId || !showSaleEvent) {
      return false;
    }

    const prevTrade =
      tradeSummaryIdx >= 1 ? tradeSummaries[tradeSummaryIdx - 1] : undefined;
    const currTrade = tradeSummaries[tradeSummaryIdx];
    if (!prevTrade && currTrade) {
      return true;
    }
    if (prevTrade && !prevTrade.saleEventId) {
      return true;
    }
    if (prevTrade && prevTrade.saleEventId !== currTrade.saleEventId) {
      return true;
    }
    return false;
  };

  const getSaleEventHeaderVariant = tradeSummaryIdx => {
    if (activeNetwork) {
      return SaleEventHeaderVariant.COMPLETE;
    }
    const prevTrade =
      tradeSummaryIdx >= 1 ? tradeSummaries[tradeSummaryIdx - 1] : undefined;
    const currTrade = tradeSummaries[tradeSummaryIdx];
    // If there is no trade before the current trade on this page
    if (!prevTrade && currTrade && pageNumber !== 1) {
      const saleEventForTradeIdx = saleEventSummaries.findIndex(
        saleEvent => saleEvent.id === currTrade.saleEventId
      );
      if (saleEventForTradeIdx !== -1) {
        // If all trades belonging to the sale event did not get included in the current page
        return saleEventSummaries[saleEventForTradeIdx].count !==
          saleEventSummaries[saleEventForTradeIdx].totalCount
          ? SaleEventHeaderVariant.CONTINUED
          : SaleEventHeaderVariant.COMPLETE;
      }
    }
    return SaleEventHeaderVariant.COMPLETE;
  };

  // Note: The following function is only used by ListTradeCard
  const shouldShowSaleEventFooter = tradeSummaryIdx => {
    if (!tradeSummaries[tradeSummaryIdx]?.saleEventId || !showSaleEvent) {
      return false;
    }
    const nextTrade =
      tradeSummaryIdx + 1 < tradeSummaries.length
        ? tradeSummaries[tradeSummaryIdx + 1]
        : undefined;
    const currTrade = tradeSummaries[tradeSummaryIdx];
    if (!nextTrade && currTrade && currTrade.saleEventId) {
      return true;
    }
    if (nextTrade && !nextTrade.saleEventId) {
      return true;
    }
    if (nextTrade && nextTrade.saleEventId !== currTrade.saleEventId) {
      return true;
    }
    return false;
  };

  const getSaleEventFooterVariant = tradeSummaryIdx => {
    if (activeNetwork) {
      return SaleEventFooterVariant.COMPLETE;
    }
    const nextTrade =
      tradeSummaryIdx + 1 < tradeSummaries.length
        ? tradeSummaries[tradeSummaryIdx + 1]
        : undefined;
    const currTrade = tradeSummaries[tradeSummaryIdx];
    // If there is no trade after the current trade on this page and there is a next page to go to
    if (!nextTrade && currTrade && hasNextPage) {
      const saleEventForTradeIdx = saleEventSummaries.findIndex(
        saleEvent => saleEvent.id === currTrade.saleEventId
      );
      if (saleEventForTradeIdx !== -1) {
        // If all trades belonging to the sale event did not get included in the current page
        return saleEventSummaries[saleEventForTradeIdx].count !==
          saleEventSummaries[saleEventForTradeIdx].totalCount
          ? SaleEventFooterVariant.CONTINUED
          : SaleEventFooterVariant.COMPLETE;
      }
    }
    return SaleEventFooterVariant.COMPLETE;
  };

  const getTradeCardEmptyHeader = () => {
    return variant === ListVariant.SEARCH ? (
      <FormattedMessage
        id="list.nav.search.no.results"
        values={{
          state: tradeState
        }}
      />
    ) : (
      <FormattedMessage
        id="list.nav.search.no.results"
        values={{
          state:
            selectedActiveList?.id === FilterNames.all.id
              ? TradeStateName[tradeState]
              : selectedActiveList?.title
        }}
      />
    );
  };

  const getTradeCardEmptyContent = () => {
    return variant === ListVariant.SEARCH ? (
      <FormattedMessage id="list.nav.search.no.results.try" />
    ) : (
      <FormattedMessage id="list.empty.content" />
    );
  };

  const getRedirectLink = (tradeId, tradeStateName) => {
    if (
      variant === ListVariant.LIST ||
      (tradeStateName &&
        (!searchIn || searchIn === SEARCH_IN.BUY) &&
        (tradeStateName === TradeStateNewName[TradeStateNew.ACTIVE] ||
          tradeStateName === TradeStateNewName[TradeStateNew.UPCOMING]))
    ) {
      return `${ROUTES.TRADE_URI}${tradeId}`;
    }
    if (isOmp) {
      return `${ROUTES.TRADE_URI}${tradeId}`;
    }
    if (process.env.REACT_APP_TR4_ENDPOINT) {
      const indexOfSep = process.env.REACT_APP_TR4_ENDPOINT.indexOf('://');
      if (indexOfSep !== -1) {
        const tr4HostRoute = process.env.REACT_APP_TR4_ENDPOINT.slice(
          indexOfSep + 1
        );
        return tr4HostRoute ? `${tr4HostRoute}trade/${tradeId}` : '';
      }
    }
    return '';
  };

  // In grid view, the impounded trade must be removed from gridTradeSummaries
  // to make sure the correct styles are applied to the trade cards.
  const onGridCardImpound = tradeId => {
    let updatedSummaries;
    const indexRemoved = gridTradeSummaries.findIndex(
      card =>
        card.tradeSummary.id === tradeId &&
        card.type !== GridCardVariant.EVENT_START
    );
    // remove impounded card from array
    const filteredSummaries = gridTradeSummaries.filter(
      (card, index) => index !== indexRemoved
    );
    // remove START and END grid cards if no cars left (all impounded) inside the EVENT
    if (
      filteredSummaries[indexRemoved].type === GridCardVariant.EVENT_END &&
      filteredSummaries[indexRemoved - 1].type === GridCardVariant.EVENT_START
    ) {
      updatedSummaries = filteredSummaries.filter(
        (card, index) => index !== indexRemoved && index !== indexRemoved - 1
      );
    } else {
      updatedSummaries = filteredSummaries;
    }
    // update gridTradeSummaries
    setGridTradeSummaries(updatedSummaries);
    if (variant === ListVariant.GRID && tradeId === selectedTrade.id) {
      dispatch(
        updateSelectedTrade({
          tradeState,
          tradeId: null
        })
      );
    }
  };

  // This function allows the sale event variant functions (see above) to be used in grid view.
  // It finds the idx of the tradeId in tradeSummaries and uses it to get the header/footer variants.
  const getGridEventVariant = (type, tradeId) => {
    const idx = tradeSummaries.findIndex(trade => trade.id === tradeId);
    return type === GridCardVariant.EVENT_START
      ? getSaleEventHeaderVariant(idx)
      : getSaleEventFooterVariant(idx);
  };

  if (isLoading) {
    return (
      <StyledTradeGrid testId={testId} variant={variant}>
        {variant !== ListVariant.GRID ? (
          <TradeCardLoading />
        ) : (
          <GridCardsLoading />
        )}
      </StyledTradeGrid>
    );
  }

  return (
    <>
      <StyledTradeGrid testId={testId} variant={variant}>
        {tradeSummaries?.length === 0 && !isLoading && (
          <TradeCardEmpty
            header={getTradeCardEmptyHeader()}
            content={getTradeCardEmptyContent()}
          />
        )}
        {variant !== ListVariant.GRID
          ? tradeSummaries &&
            tradeSummaries.map((tradeSummary, tradeSummaryIdx) => (
              <React.Fragment key={tradeSummary.id}>
                <>
                  {shouldShowSaleEventHeader(tradeSummaryIdx) &&
                    tradeSummary?.saleEventId && (
                      <SaleEventHeader
                        testId={`saleEventHeader-${tradeSummary.id}`}
                        tradeSummary={tradeSummary}
                        tradeState={tradeState}
                        className={`saleEventHeader-${tradeSummary?.saleEventId}`}
                        variant={getSaleEventHeaderVariant(tradeSummaryIdx)}
                        listVariant={variant}
                      />
                    )}
                </>
                <StyledTradeGridWrapper
                  className={`saleEventWrapper-${tradeSummary?.saleEventId}`}
                  id={`saleEventWrapper-${tradeSummary?.saleEventId}`}
                  isInSaleEvent={!!(showSaleEvent && tradeSummary?.saleEventId)}
                >
                  <TradeCard
                    id={`tradeCard-${tradeSummary.id}`}
                    key={`list-tradeCard-${tradeSummary.id}`}
                    testId={`tradeCard-${tradeSummary.id}`}
                    tradeSummary={tradeSummary}
                    onSelect={tradeSelectionHandler}
                    variant={variant}
                    isInSaleEvent={
                      !!(showSaleEvent && tradeSummary?.saleEventId)
                    }
                    isLastTradeInSaleEvent={shouldShowSaleEventFooter(
                      tradeSummaryIdx
                    )}
                    redirectLink={getRedirectLink(
                      tradeSummary.id,
                      tradeSummary?.tradeStateName
                    )}
                    onConfirmOpen={onConfirmOpen}
                    tradeState={tradeState}
                    pendingActiveProxy={pendingActiveProxy}
                    bidModalRef={bidModalRef}
                    isOmp={isOmp}
                  />
                </StyledTradeGridWrapper>
                <>
                  {shouldShowSaleEventFooter(tradeSummaryIdx) &&
                    tradeSummary?.saleEventId && (
                      <SaleEventFooter
                        testId={`saleEventFooter-${tradeSummary.id}`}
                        className={`saleEventFooter-${tradeSummary?.saleEventId}`}
                        saleEventId={tradeSummary?.saleEventId}
                        variant={getSaleEventFooterVariant(tradeSummaryIdx)}
                      />
                    )}
                </>
              </React.Fragment>
            ))
          : gridTradeSummaries &&
            gridTradeSummaries.map(({ type, tradeSummary }, id) => {
              const cardTypeToKey = {
                [GridCardVariant.EVENT_START]: `saleEventStart-${tradeSummary?.saleEventId}`,
                [GridCardVariant.EVENT_END]: `saleEventEnd-${tradeSummary?.saleEventId}`,
                [GridCardVariant.EVENT_TRADE]: `grid-event-tradeCard-${tradeSummary.id}`,
                [GridCardVariant.NON_EVENT_TRADE]: `grid-tradeCard-${tradeSummary.id}`
              };
              return (
                <TradeCard
                  key={cardTypeToKey[type]}
                  id={cardTypeToKey[type]}
                  type={type}
                  variant={variant}
                  eventVariant={
                    (type === GridCardVariant.EVENT_START ||
                      type === GridCardVariant.EVENT_END) &&
                    getGridEventVariant(type, tradeSummary.id)
                  }
                  positionIndex={id + 1}
                  tradeSummary={tradeSummary}
                  onImpound={onGridCardImpound}
                  tradeState={tradeState}
                  showSaleEvent={showSaleEvent}
                  bidModalRef={bidModalRef}
                  onSelect={tradeSelectionHandler}
                  pendingActiveProxy={pendingActiveProxy}
                  isOmp={isOmp}
                />
              );
            })}
      </StyledTradeGrid>
    </>
  );
};

TradeGrid.propTypes = propTypes;
TradeGrid.defaultProps = defaultProps;

export default TradeGrid;
